import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SharedModule } from '@shared/modules/shared.module';
import { GphAssignment, GphProgram } from '../../models/gph-ops.model';
import { GphOpsService } from '../../services/gph-ops.service';

@Component({
    selector: 'gph-tools',
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule],
    templateUrl: './gph-tools.component.html',
    styleUrls: ['./gph-tools.component.scss'],
    providers: [MessageService]
})
export class GphToolsComponent implements OnInit {
    programs: GphProgram[] = [];
    assignments: GphAssignment[] = [];
    loading = false;
    actionBusy = false;
    assignDialog = false;
    removeDialog = false;
    selectedAssignment?: GphAssignment;
    selectedProgram?: GphProgram;
    riderId?: number;
    rosterFromDate = this.manilaDate(new Date());
    rosterToDate = this.manilaDate(new Date());
    reason = '';
    search = '';
    selectedIncentiveId?: number;

    constructor(private service: GphOpsService, private messages: MessageService) {}

    async ngOnInit(): Promise<void> { await this.reload(); }

    async reload(): Promise<void> {
        this.loading = true;
        try {
            const [programs, assignments] = await Promise.all([this.service.getPrograms(), this.service.getAssignments()]);
            if (!programs?.Success) { this.error(programs?.ErrorMessage || 'Unable to load GPH shifts.'); return; }
            if (!assignments?.Success) { this.error(assignments?.ErrorMessage || 'Unable to load GPH roster.'); return; }
            this.programs = programs.Items;
            this.assignments = assignments.Items;
        } catch { this.error('Unable to load GPH Tools. Please try again.'); }
        finally { this.loading = false; }
    }

    openAssign(): void {
        this.selectedProgram = this.programs[0];
        this.riderId = undefined;
        this.setDefaultRosterDates();
        this.reason = '';
        this.assignDialog = true;
    }

    onAssignProgramChange(): void {
        if (this.selectedProgram) {
            this.setDefaultRosterDates();
        }
    }

    openRemove(assignment: GphAssignment): void {
        this.selectedAssignment = assignment;
        this.reason = '';
        this.removeDialog = true;
    }

    get filteredAssignments(): GphAssignment[] {
        const search = this.search.trim().toLocaleLowerCase();
        return this.assignments.filter(assignment =>
            (!this.selectedIncentiveId || assignment.IncentiveId === this.selectedIncentiveId)
            && (!search
                || String(assignment.RiderId).includes(search)
                || String(assignment.XpressUserId).includes(search)
                || assignment.RiderName.toLocaleLowerCase().includes(search)
                || assignment.IncentiveName.toLocaleLowerCase().includes(search)));
    }

    async assign(): Promise<void> {
        if (!this.selectedProgram || !this.riderId || !this.rosterFromDate || !this.rosterToDate) { return; }
        const range = this.rosterRangeForManilaDates(this.rosterFromDate, this.rosterToDate);
        if (!range) { this.error('Roster end date must be on or after the start date.'); return; }
        this.actionBusy = true;
        try {
            const response = await this.service.assignRider({
                IncentiveId: this.selectedProgram.IncentiveId,
                RiderId: this.riderId,
                EffectiveFromUtc: range.from.toISOString(),
                EffectiveToUtc: range.to.toISOString(),
                Reason: this.reason.trim() || null
            });
            if (!response?.Success) { this.error(response?.ErrorMessage || 'GPH assignment was not created.'); return; }
            this.success(`Driver ${this.riderId} was assigned to ${this.selectedProgram.Name}.`);
            this.assignDialog = false;
            await this.reload();
        } catch { this.error('GPH assignment was not created. Please try again.'); }
        finally { this.actionBusy = false; }
    }

    async remove(): Promise<void> {
        if (!this.selectedAssignment) { return; }
        this.actionBusy = true;
        try {
            const response = await this.service.unassignRider({
                IncentiveId: this.selectedAssignment.IncentiveId,
                RiderId: this.selectedAssignment.RiderId,
                Reason: this.reason.trim() || null
            });
            if (!response?.Success) { this.error(response?.ErrorMessage || 'GPH assignment was not removed.'); return; }
            this.success(`Driver ${this.selectedAssignment.RiderId} was removed from GPH.`);
            this.removeDialog = false;
            await this.reload();
        } catch { this.error('GPH assignment was not removed. Please try again.'); }
        finally { this.actionBusy = false; }
    }

    shiftLabel(program: GphProgram): string { return `${program.StartTime.slice(0, 5)}–${program.EndTime.slice(0, 5)} Manila`; }

    shiftWindow(incentiveId: number): string {
        const program = this.programs.find(item => item.IncentiveId === incentiveId);
        return program ? `${program.StartTime.slice(0, 5)}–${program.EndTime.slice(0, 5)} PHT` : '—';
    }

    private rosterRangeForManilaDates(fromDate: string, toDate: string): { from: Date; to: Date } | undefined {
        if (toDate < fromDate) { return undefined; }
        return {
            from: this.manilaMidnight(fromDate),
            to: this.manilaMidnight(this.addManilaDays(toDate, 1))
        };
    }

    private manilaMidnight(localDate: string): Date {
        const [year, month, day] = localDate.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day - 1, 16));
    }

    private manilaDate(value: Date): string {
        const manila = new Date(value.getTime() + 8 * 60 * 60 * 1000);
        return manila.toISOString().slice(0, 10);
    }

    private defaultShiftDate(program: GphProgram): string {
        const now = new Date();
        const manilaNow = new Date(now.getTime() + 8 * 60 * 60 * 1000);
        const [startHour, startMinute] = program.StartTime.split(':').map(Number);
        const currentMinutes = manilaNow.getUTCHours() * 60 + manilaNow.getUTCMinutes();
        const startMinutes = startHour * 60 + startMinute;

        // A shift that has already started is not offered as a new roster date. The server also
        // rejects that past window, but selecting the next viable date here avoids a confusing
        // backdate error for the normal workflow.
        return this.manilaDate(new Date(now.getTime() + (currentMinutes >= startMinutes ? 24 : 0) * 60 * 60 * 1000));
    }

    private setDefaultRosterDates(): void {
        const firstDate = this.selectedProgram ? this.defaultShiftDate(this.selectedProgram) : this.manilaDate(new Date());
        this.rosterFromDate = firstDate;
        this.rosterToDate = this.addManilaDays(firstDate, 6);
    }

    private addManilaDays(localDate: string, days: number): string {
        const [year, month, day] = localDate.split('-').map(Number);
        return this.manilaDate(new Date(Date.UTC(year, month - 1, day + days)));
    }
    private success(detail: string): void { this.messages.add({ severity: 'success', summary: 'GPH Tools', detail }); }
    private error(detail: string): void { this.messages.add({ severity: 'error', summary: 'GPH Tools', detail }); }
}
